import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

const rowStyle = css`
	display: flex;
	align-items: center;
	gap: 22px;
`;

/** No token in `theme.ts` covers a translucent-white overlay on `colors.surfaceInverted`: this exact
 * value comes straight from the reference mockup (`Dixit - Refonte ADAR v8.dc.html`), which uses the same
 * literal rather than a design token for this one case. */
const pauseButtonOverlayColor = 'rgba(255, 255, 255, 0.14)';

const circularButtonBaseStyle = css`
	border: none;
	border-radius: 50%;
	color: ${theme.colors.inkOnDark};
	font-family: ${theme.fonts.body};
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const pauseButtonStyle = css`
	${circularButtonBaseStyle}
	width: 4.625rem;
	height: 4.625rem;
	background-color: ${pauseButtonOverlayColor};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
`;

const stopButtonStyle = css`
	${circularButtonBaseStyle}
	width: 6rem;
	height: 6rem;
	background-color: ${theme.colors.recording};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
`;

type Props = {
	readonly phase: 'recording' | 'paused';
	/** A dictated note has no pause: only its stop control is drawn. */
	readonly canPause?: boolean;
	readonly stopLabel?: string;
	readonly isStopping: boolean;
	readonly onTogglePause: () => void;
	readonly onStop: () => void;
};

/**
 * The two round controls of the recording screen: "Pause"/"Reprendre" and "Arrêter" ("Terminer" for a note), the latter carrying
 * `colors.recording` as its only color (`reference/design-direction.md` §5 — never decorative).
 * Both disable together while a stop is already in flight, so a second click cannot race the first.
 */
const RecordingControls: React.FC<Props> = ({
	phase,
	canPause = true,
	stopLabel = 'Arrêter',
	isStopping,
	onTogglePause,
	onStop,
}) => {
	const pauseLabel = phase === 'paused' ? 'Reprendre' : 'Pause';

	return (
		<div css={rowStyle}>
			<ConditionalLoader data={canPause}>
				<button type="button" css={pauseButtonStyle} disabled={isStopping} onClick={onTogglePause}>
					{pauseLabel}
				</button>
			</ConditionalLoader>
			<button type="button" css={stopButtonStyle} disabled={isStopping} onClick={onStop}>
				{stopLabel}
			</button>
		</div>
	);
};

export default RecordingControls;
